import type { ReactionsTable } from '../drizzle';

export type ReactionRecord = typeof ReactionsTable.$inferSelect;
export type ReactionInsert = typeof ReactionsTable.$inferInsert;
export type ReactionEmoji = (typeof ReactionsTable.$inferSelect)['emoji'];
export const REACTION_EMOJIS: ReactionEmoji[] = ['👍', '👎', '😄', '🎉', '😕', '❤️', '🚀', '👀'] as const;

// REST API content values
export const GITHUB_REST_CONTENT = ['+1', '-1', 'laugh', 'hooray', 'confused', 'heart', 'rocket', 'eyes'] as const;
export type GitHubRestReactionContent = (typeof GITHUB_REST_CONTENT)[number];

// GraphQL API content values
export const GITHUB_GRAPHQL_CONTENT = [
  'THUMBS_UP',
  'THUMBS_DOWN',
  'LAUGH',
  'HOORAY',
  'CONFUSED',
  'HEART',
  'ROCKET',
  'EYES',
] as const;
export type GitHubGraphQLReactionContent = (typeof GITHUB_GRAPHQL_CONTENT)[number];

// Emoji to REST API content mapping
export const EMOJI_TO_REST_MAP: Record<ReactionEmoji, GitHubRestReactionContent> = {
  '👍': '+1',
  '👎': '-1',
  '😄': 'laugh',
  '🎉': 'hooray',
  '😕': 'confused',
  '❤️': 'heart',
  '🚀': 'rocket',
  '👀': 'eyes',
} as const;

// Emoji to GraphQL content mapping
export const EMOJI_TO_GRAPHQL_MAP: Record<ReactionEmoji, GitHubGraphQLReactionContent> = {
  '👍': 'THUMBS_UP',
  '👎': 'THUMBS_DOWN',
  '😄': 'LAUGH',
  '🎉': 'HOORAY',
  '😕': 'CONFUSED',
  '❤️': 'HEART',
  '🚀': 'ROCKET',
  '👀': 'EYES',
} as const;

// Reverse mappings
export const REST_TO_EMOJI_MAP = Object.fromEntries(
  Object.entries(EMOJI_TO_REST_MAP).map(([emoji, content]) => [content, emoji])
) as Record<GitHubRestReactionContent, ReactionEmoji>;

export const GRAPHQL_TO_EMOJI_MAP = Object.fromEntries(
  Object.entries(EMOJI_TO_GRAPHQL_MAP).map(([emoji, content]) => [content, emoji])
) as Record<GitHubGraphQLReactionContent, ReactionEmoji>;

// Converter functions
export function emojiToRestContent(emoji: ReactionEmoji): GitHubRestReactionContent | undefined {
  return EMOJI_TO_REST_MAP[emoji];
}

export function emojiToGraphQLContent(emoji: ReactionEmoji): GitHubGraphQLReactionContent | undefined {
  return EMOJI_TO_GRAPHQL_MAP[emoji];
}

export function restContentToEmoji(content: GitHubRestReactionContent): ReactionEmoji | undefined {
  return REST_TO_EMOJI_MAP[content];
}

export function graphQLContentToEmoji(content: GitHubGraphQLReactionContent): ReactionEmoji | undefined {
  return GRAPHQL_TO_EMOJI_MAP[content];
}

// Legacy functions for backward compatibility
export function contentToEmoji(content: GitHubRestReactionContent): ReactionEmoji | undefined {
  return restContentToEmoji(content);
}

export function emojiToContent(emoji: ReactionEmoji): GitHubRestReactionContent | undefined {
  return emojiToRestContent(emoji);
}

export type ReactionSummary = {
  emoji: ReactionEmoji;
  count: number;
  userReacted: boolean;
};
